import { Component, inject, Input, OnInit } from "@angular/core";
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from "@angular/forms";
import { NgbActiveModal, NgbDateStruct } from "@ng-bootstrap/ng-bootstrap";
import { TranslatePipe, TranslateService } from "@ngx-translate/core";
import { ToastrService } from "ngx-toastr";

import { dateRangeValidator } from "../../../../core/validators";
import { PageMode } from "../../../constants/enums";
import { AgmTask, ValidatorModelType, ValueText } from "../../../models";
import { ValidatorApiService } from "../../../services";
import { FormCheckboxComponent, FormDropdownComponent, FormInputComponent, FormInputDateComponent } from "../../form";

@Component({
  selector: "adaa-agm-task-modal",
  imports: [
    TranslatePipe,
    ReactiveFormsModule,
    FormCheckboxComponent,
    FormDropdownComponent,
    FormInputComponent,
    FormInputDateComponent,
  ],
  templateUrl: "./agm-task-modal.html",
  styleUrl: "./agm-task-modal.scss",
})
export class AgmTaskModalComponent implements OnInit {
  private readonly _formBuilder = inject(FormBuilder);
  private readonly _translateService = inject(TranslateService);
  private readonly _toastrService = inject(ToastrService);
  private readonly _validatorApiService = inject(ValidatorApiService);
  readonly modal = inject(NgbActiveModal);

  @Input() task: AgmTask;
  @Input() pageMode: PageMode;
  @Input() formValidation: ValidatorModelType;
  @Input() entities: ValueText[] = [];
  @Input() otherEntities: ValueText[] = [];
  @Input() parentEntityId: number | undefined = undefined;
  @Input() parentStartDate: number | undefined = undefined;
  @Input() parentEndDate: number | undefined = undefined;

  taskForm: FormGroup;
  submitted: boolean = false;
  PageMode = PageMode;

  public get taskLimits() {
    const minDate = this.parentStartDate ? new Date(this.parentStartDate) : undefined;
    const maxDate = this.parentEndDate ? new Date(this.parentEndDate) : undefined;

    return {
      min: minDate
        ? ({
            year: minDate.getFullYear(),
            month: minDate.getMonth() + 1,
            day: minDate.getDate(),
          } as NgbDateStruct)
        : null,
      max: maxDate
        ? ({
            year: maxDate.getFullYear(),
            month: maxDate.getMonth() + 1,
            day: maxDate.getDate(),
          } as NgbDateStruct)
        : null,
    };
  }

  public ngOnInit(): void {
    this._prepareForm();
    this.taskForm.patchValue(this.task);

    if (this.task?.entityId) this.entityIdChanged(this.task.entityId);
  }

  public entityIdChanged(value: number) {
    if (this.parentEntityId && value === this.parentEntityId) this.taskForm.get("otherEntityId")?.enable();
    else {
      this.taskForm.get("otherEntityId")?.disable();
      this.taskForm.get("otherEntityId")?.setValue(null);
      this.taskForm.get("otherEntityNameAE")?.setValue(null);
      this.taskForm.get("otherEntityNameEN")?.setValue(null);
    }

    const entity = this.entities.find((e) => e.value === value);
    if (entity) {
      this.taskForm.get("entityNameAE")?.setValue(entity.text);
      this.taskForm.get("entityNameEN")?.setValue(entity.text);
    }
  }

  public otherEntityIdChanged(value: number) {
    const entity = this.otherEntities.find((e) => e.value === value);
    if (entity) {
      this.taskForm.get("otherEntityNameAE")?.setValue(entity.text);
      this.taskForm.get("otherEntityNameEN")?.setValue(entity.text);
    }
  }

  public save(): void {
    this.submitted = true;

    if (!this.taskForm.valid) {
      this._toastrService.warning(this._translateService.instant("notification.warning.missing_info"));
      return;
    }

    this.modal.close(this.taskForm.getRawValue());
  }

  private _prepareForm() {
    this.taskForm = this._formBuilder.group(
      {
        completed: { value: false, disabled: false },
        startDate: { value: null, disabled: false },
        endDate: { value: null, disabled: false },
        nameAE: { value: null, disabled: false },
        nameEN: { value: null, disabled: false },
        dscAE: { value: null, disabled: false },
        dscEN: { value: null, disabled: false },
        entityId: { value: null, disabled: false },
        entityNameAE: { value: null, disabled: false },
        entityNameEN: { value: null, disabled: false },
        otherEntityId: { value: null, disabled: true },
        otherEntityNameAE: { value: null, disabled: true },
        otherEntityNameEN: { value: null, disabled: true },
        createdBy: { value: null, disabled: false },
        updatedBy: { value: null, disabled: false },
        createdByUserNameAE: { value: null, disabled: false },
        createdByUserNameEN: { value: null, disabled: false },
        creationDate: { value: null, disabled: false },
        fakeId: { value: null, disabled: false },
        id: { value: null, disabled: false },
        status: { value: null, disabled: false },
      },
      {
        validators: Validators.compose([dateRangeValidator("startDate", "endDate")]),
      }
    );

    this.taskForm = this._validatorApiService.setValidationFromData(this.formValidation, this.taskForm);
  }
}
